import { create, StoreApi, UseBoundStore } from 'zustand';
import { toast } from 'react-toastify';

import getErrorMessage from '../utils/error';
import { IInsuranceStore } from '../types/types';
import { createInsurance, deleteInsurance, getInsurances, updateInsurance } from '../utils/api';

const useInsurance: UseBoundStore<StoreApi<IInsuranceStore>> = create((set) => ({
  insurances: [],
  saveLoading: false,
  fetchLoading: false,
  removeLoading: false,
  hasFetched: false,

  fetchInsurances: async () => {
    try {
      set({ fetchLoading: true });
      const { success, message, data } = await getInsurances();
      if (!success) throw new Error(message);
      set({ insurances: data, hasFetched: true });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ fetchLoading: false });
    }
  },

  addInsurance: async (insuranceData) => {
    try {
      set({ saveLoading: true });
      const { success, message, data } = await createInsurance(insuranceData);
      if (!success) throw new Error(message);
      set((state) => ({ insurances: state.insurances ? [...state.insurances, data] : [data] }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ saveLoading: false });
    }
  },

  editInsurance: async (id, insuranceData) => {
    try {
      set({ saveLoading: true });
      const { success, message, data } = await updateInsurance(id, insuranceData);
      if (!success) throw new Error(message);
      set((state) => ({
        insurances: state.insurances?.map((insurance) => (insurance.insurance_id === id ? data : insurance)),
      }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ saveLoading: false });
    }
  },

  removeInsurance: async (id) => {
    try {
      set({ removeLoading: true });
      const { success, message } = await deleteInsurance(id);
      if (!success) throw new Error(message);
      set((state) => ({
        insurances: state.insurances?.filter((insurance) => insurance.insurance_id !== id),
      }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ removeLoading: false });
    }
  },
}));

export default useInsurance;
